import { useEffect, useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BedDouble,
  Building,
  Building2,
  Check,
  Factory,
  Landmark,
  Plus,
  ShoppingCart,
  Tractor,
} from "lucide-react";
import Seo from "@/components/v2/Seo";
import TraceCard from "@/components/v2/TraceCard";
import { Section } from "@/components/v2/blocks";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";

/* ────────────────────────────────────────────────────────────────────────────
   /professionnels/primes-cee/secteur/:slug — pages sectorielles Primes CEE.
   La liste SECTEURS_CEE alimente aussi la section « Votre secteur d'activité »
   de /primes-cee. Contenus volontairement sans codes de fiches CEE (ils
   évoluent avec la P6) : on décrit les opérations, le conseiller précise.
   ──────────────────────────────────────────────────────────────────────────── */

const P = "/img/pictogrammes_energy_market_svg";
const TRACE_COLORS = ["#2E7D62", "#7AB317", "#0E9C8A", "#2F7DB8"];

export const SECTEURS_CEE = [
  {
    slug: "sante",
    heroIcon: Plus,
    kw: "Hôpitaux · Cliniques · EHPAD",
    accroche: "Réduisez le coût énergétique de votre établissement",
    chapo: "Financez vos équipements de chauffage, ventilation, froid et eau chaude grâce aux primes CEE, sans mobiliser votre budget de soins.",
    nom: "Santé",
    picto: `${P}/cee_secteur_sante.svg`,
    h1: "Primes CEE pour le secteur de la santé",
    metaDesc: "Hôpitaux, cliniques, EHPAD : financez vos travaux d'efficacité énergétique grâce aux CEE. Energy Market met les offres de valorisation en concurrence.",
    intro: "Hôpitaux, cliniques et EHPAD fonctionnent 24 h/24 : chauffage, eau chaude sanitaire, ventilation et froid pèsent lourdement sur les budgets. Les CEE financent une part importante de ces travaux — sans immobiliser vos ressources dédiées au soin.",
    operations: [
      { title: "GTB & pilotage énergétique", text: "Installation ou amélioration d'une gestion technique du bâtiment pour piloter chauffage, ventilation et climatisation au plus juste, service par service." },
      { title: "Production de chaleur performante", text: "Pompes à chaleur, chaufferies performantes ou raccordement à un réseau de chaleur pour le chauffage et l'eau chaude sanitaire des établissements." },
      { title: "Calorifugeage des réseaux", text: "Isolation des réseaux hydrauliques et des points singuliers : un gisement rapide, peu invasif et fortement valorisé en CEE." },
      { title: "Récupération de chaleur", text: "Valorisation de la chaleur fatale des groupes froid, blanchisseries et process pour préchauffer l'eau chaude sanitaire." },
    ],
  },
  {
    slug: "hotellerie",
    heroIcon: BedDouble,
    kw: "Hôtels · Restaurants · Résidences",
    accroche: "L'énergie au service du confort, pas de la facture",
    chapo: "ECS, cuisine, climatisation : financez vos équipements performants grâce aux primes CEE, sans dégrader l'expérience client.",
    nom: "Hôtellerie & restauration",
    picto: `${P}/cee_secteur_hotellerie.svg`,
    h1: "Primes CEE pour l'hôtellerie et la restauration",
    metaDesc: "Hôtels et restaurants : réduisez vos consommations de chauffage, ECS et froid grâce aux primes CEE, valorisées au meilleur prix par Energy Market.",
    intro: "Confort des clients d'un côté, facture énergétique de l'autre : l'hôtellerie-restauration cumule chauffage, eau chaude sanitaire abondante, cuisine et froid. Les CEE permettent d'accélérer la rénovation sans dégrader l'expérience client.",
    operations: [
      { title: "Eau chaude sanitaire performante", text: "Pompes à chaleur dédiées à l'ECS et récupération de chaleur pour un poste qui tourne toute l'année dans un hôtel." },
      { title: "Régulation & GTB", text: "Pilotage du chauffage et de la climatisation par zone ou par chambre : consommer uniquement quand la chambre est occupée." },
      { title: "Isolation de l'enveloppe", text: "Toitures, combles et réseaux : des chantiers compatibles avec l'exploitation, planifiables en basse saison." },
      { title: "Froid & cuisines professionnelles", text: "Fermeture des meubles frigorifiques, récupération de chaleur sur les groupes froid et équipements de cuisine performants." },
    ],
  },
  {
    slug: "commerce",
    heroIcon: ShoppingCart,
    kw: "Magasins · GMS · Retail",
    accroche: "Allégez la facture énergétique de vos points de vente",
    chapo: "Froid commercial, GTB, éclairage : les primes CEE financent vos travaux, magasin ouvert, sur un ou plusieurs sites.",
    nom: "Commerce & distribution",
    picto: `${P}/cee_secteur_commerce.svg`,
    h1: "Primes CEE pour le commerce et la distribution",
    metaDesc: "Magasins, GMS, retail : froid commercial, GTB, isolation — financez vos travaux avec les CEE, mis en concurrence par Energy Market.",
    intro: "Du commerce de proximité à la grande distribution, le froid commercial et le conditionnement d'air dominent la facture. Beaucoup d'opérations CEE du secteur sont rapides à déployer, magasin ouvert.",
    operations: [
      { title: "Froid commercial", text: "Fermeture des meubles frigorifiques par portes vitrées et modernisation des groupes froid : un des gisements les mieux valorisés du secteur." },
      { title: "Récupération de chaleur", text: "La chaleur rejetée par les groupes froid chauffe la surface de vente ou l'eau chaude — deux consommations pour le prix d'une." },
      { title: "GTB & régulation", text: "Pilotage centralisé du chauffage, de la climatisation et des rideaux d'air chaud sur un ou plusieurs points de vente." },
      { title: "Enveloppe du bâtiment", text: "Isolation des toitures et déstratification des grands volumes pour limiter les besoins de chauffage des surfaces commerciales." },
    ],
  },
  {
    slug: "bureaux",
    heroIcon: Building2,
    kw: "Bureaux · Sièges · Parcs tertiaires",
    accroche: "Des bureaux sobres, conformes et attractifs",
    chapo: "GTB, CVC, isolation : les primes CEE accélèrent votre mise en conformité Éco Énergie Tertiaire sans grever vos budgets.",
    nom: "Bureaux & tertiaire",
    picto: `${P}/pro_tertiaire.svg`,
    h1: "Primes CEE pour les bureaux et le tertiaire",
    metaDesc: "Immeubles de bureaux : GTB, CVC, isolation — les CEE financent vos travaux et accélèrent votre conformité au dispositif Éco Énergie Tertiaire.",
    intro: "Entre obligations du dispositif Éco Énergie Tertiaire et attentes des occupants, les immeubles de bureaux doivent réduire leurs consommations. Les CEE solvabilisent une grande partie des travaux nécessaires.",
    operations: [
      { title: "GTB classe A ou B", text: "La gestion technique du bâtiment est l'opération phare du tertiaire : fortement bonifiée, elle pilote CVC et éclairage et objective vos gains." },
      { title: "CVC performant", text: "Pompes à chaleur, groupes performants et ventilation modulée pour remplacer les équipements vieillissants." },
      { title: "Isolation de l'enveloppe", text: "Toitures, murs et planchers : réduire les besoins avant de produire mieux." },
      { title: "Raccordement réseau de chaleur", text: "Le Coup de pouce dédié bonifie le raccordement des bâtiments tertiaires aux réseaux de chaleur renouvelables." },
    ],
  },
  {
    slug: "industrie",
    heroIcon: Factory,
    kw: "Usines · Process · Multi-sites",
    accroche: "Valorisez chaque kWh cumac de vos sites industriels",
    chapo: "Moteurs, air comprimé, chaleur fatale : transformez vos gisements d'efficacité en primes CEE négociées au meilleur prix.",
    nom: "Industrie",
    picto: `${P}/pro_industrie.svg`,
    h1: "Primes CEE pour l'industrie",
    metaDesc: "Sites industriels : moteurs, air comprimé, chaleur fatale — valorisez vos kWh cumac au meilleur prix avec Energy Market.",
    intro: "L'industrie dispose des gisements CEE les plus massifs : utilités, process et bâtiments. Fiches standardisées ou opérations spécifiques, chaque site a des kWh cumac à valoriser — à des prix qui varient fortement selon l'acheteur.",
    operations: [
      { title: "Moteurs & variation de vitesse", text: "Systèmes moto-régulés et variation électronique de vitesse sur pompes, ventilateurs et compresseurs." },
      { title: "Air comprimé", text: "Récupération de chaleur sur compresseurs, séquenceurs et traque des fuites : des retours sur investissement souvent inférieurs à deux ans." },
      { title: "Chaleur fatale", text: "Récupération de la chaleur des fumées, groupes froid et process pour la réinjecter dans vos utilités ou un réseau." },
      { title: "Isolation des points singuliers", text: "Matelas isolants sur vannes et échangeurs : simple, rapide, et systématiquement rentable." },
    ],
  },
  {
    slug: "agriculture",
    heroIcon: Tractor,
    kw: "Serres · Élevage · Exploitations",
    accroche: "Réduisez durablement les charges de votre exploitation",
    chapo: "Serres, tanks à lait, bâtiments d'élevage : des fiches CEE dédiées financent vos équipements performants.",
    nom: "Agriculture",
    picto: `${P}/cee_secteur_agriculture.svg`,
    h1: "Primes CEE pour l'agriculture",
    metaDesc: "Exploitations agricoles, serres, élevage : les CEE financent vos équipements performants. Energy Market valorise vos opérations au meilleur prix.",
    intro: "Serres, bâtiments d'élevage, laiteries : l'agriculture a ses propres fiches CEE, souvent méconnues. Elles financent des équipements qui réduisent durablement vos charges.",
    operations: [
      { title: "Serres performantes", text: "Écrans thermiques, double parois et pilotage climatique pour réduire le chauffage des serres maraîchères et horticoles." },
      { title: "Récupération sur tank à lait", text: "Pré-refroidisseurs et récupérateurs de chaleur sur tanks à lait : l'eau chaude de la traite quasi gratuite." },
      { title: "Bâtiments d'élevage", text: "Chauffage performant, régulation et isolation des bâtiments porcins et avicoles." },
      { title: "Séchage & process", text: "Optimisation du séchage en grange et des process énergivores de l'exploitation." },
    ],
  },
  {
    slug: "collectivites",
    heroIcon: Landmark,
    kw: "Communes · EPCI · Départements",
    accroche: "Financez la rénovation de votre patrimoine public",
    chapo: "Écoles, gymnases, éclairage public : les primes CEE complètent vos subventions et allègent le reste à charge.",
    nom: "Collectivités",
    picto: `${P}/cee_dispositif_etat.svg`,
    h1: "Primes CEE pour les collectivités",
    metaDesc: "Communes, EPCI, départements : écoles, gymnases, éclairage public — les CEE financent la rénovation de votre patrimoine. Étude gratuite.",
    intro: "Écoles, gymnases, mairies, éclairage public : le patrimoine des collectivités est un gisement CEE majeur. Les primes complètent vos subventions et allègent la part restant à financer par le budget public.",
    operations: [
      { title: "Bâtiments publics", text: "Isolation, GTB et chauffage performant pour écoles, équipements sportifs et bâtiments administratifs." },
      { title: "Éclairage public", text: "Rénovation des luminaires vétustes : un poste visible des administrés et éligible aux CEE." },
      { title: "Chaufferies & réseaux de chaleur", text: "Chaufferies biomasse, raccordements et extensions de réseaux de chaleur renouvelables, avec bonifications dédiées." },
      { title: "Piscines & équipements spécifiques", text: "Couvertures de bassins, récupération de chaleur et déshumidification performante des centres aquatiques." },
    ],
  },
  {
    slug: "coproprietes",
    heroIcon: Building,
    kw: "Syndics · Bailleurs · Copropriétés",
    accroche: "Faites voter les travaux grâce à des primes jusqu'à ×5",
    chapo: "Coup de pouce chauffage collectif, rénovation d'ampleur : des bonifications décisives pour convaincre l'assemblée générale.",
    nom: "Copropriétés & résidentiel collectif",
    picto: `${P}/cee_tous_profils.svg`,
    h1: "Primes CEE pour les copropriétés et le résidentiel collectif",
    metaDesc: "Syndics, bailleurs, copropriétés : Coup de pouce chauffage collectif, rénovation d'ampleur — Energy Market obtient la meilleure prime CEE.",
    intro: "Le résidentiel collectif concentre les bonifications les plus fortes du dispositif : le Coup de pouce chauffage collectif multiplie jusqu'à ×5 la prime standard. Un levier décisif pour faire voter les travaux en assemblée générale.",
    operations: [
      { title: "Coup de pouce chauffage collectif", text: "PAC collectives, chaudières biomasse et raccordement aux réseaux de chaleur : primes multipliées de ×3 à ×5 selon l'équipement." },
      { title: "Rénovation d'ampleur", text: "Bouquets de travaux avec gain de classes énergétiques au DPE collectif, fortement valorisés sur toute la 6ᵉ période." },
      { title: "Calorifugeage & équilibrage", text: "Isolation des réseaux de chauffage et d'ECS en parties communes : rapide, sans travaux dans les logements." },
      { title: "Isolation de l'enveloppe", text: "Toitures-terrasses, planchers bas et façades pour réduire durablement les charges de tous les copropriétaires." },
    ],
  },
];

export default function SecteurPrimesCee() {
  const { slug } = useParams();

  // Révélation de la photo au scroll : le voile vert s'efface et le zoom se
  // relâche à mesure qu'on descend dans le hero. On expose la progression
  // (0 → 1) en variable CSS pour laisser le rendu au compositeur.
  const heroRef = useRef(null);
  const photoRef = useRef(null);
  useEffect(() => {
    const hero = heroRef.current;
    const photo = photoRef.current;
    if (!hero || !photo) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      photo.style.setProperty("--reveal", "1");
      return;
    }
    let raf = 0;
    const update = () => {
      raf = 0;
      const r = hero.getBoundingClientRect();
      const span = Math.max(r.height * 0.45, 1);
      const p = Math.min(1, Math.max(0, -r.top / span));
      photo.style.setProperty("--reveal", String(p));
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [slug]);

  const s = SECTEURS_CEE.find((x) => x.slug === slug);
  if (!s) return <Navigate to="/professionnels/primes-cee" replace />;

  return (
    <div>
      <Seo title={`${s.nom} — Primes CEE`} description={s.metaDesc} path={`/professionnels/primes-cee/secteur/${s.slug}`} />

      {/* Bandeau sombre — photo pleine hauteur à droite, découpe en arc */}
      <section
        ref={heroRef}
        className="relative -mt-20 overflow-hidden bg-primary text-primary-foreground lg:flex lg:min-h-[100svh] lg:items-center"
        style={{ backgroundImage: "radial-gradient(circle at 20% 15%, rgba(8,90,66,0.5), transparent 45%)" }}
      >
        {/* Photo du secteur (desktop), masquée si absente */}
        <div
          ref={photoRef}
          className="absolute inset-y-0 right-0 hidden w-[56%] overflow-hidden border-b border-l border-accent/50 lg:block"
          style={{
            /* L'arc mord dans la photo au départ, puis se retire au scroll :
               c'est la découpe qui révèle l'image, sans rien assombrir. */
            borderBottomLeftRadius: "calc((58 + 46 * (1 - var(--reveal, 0))) * 1%) 100%",
            willChange: "border-radius",
          }}
        >
          <img
            src={`/img/secteurs-cee/${s.slug}.webp`}
            alt=""
            aria-hidden
            loading="eager"
            decoding="async"
            className="h-full w-full object-cover will-change-transform"
            style={{ transform: "scale(calc(1.12 - 0.12 * var(--reveal, 0)))" }}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.parentElement.style.display = "none"; }}
          />
        </div>
        {/* Carte « Votre projet, simplifié » (desktop) */}
        <div className="absolute bottom-28 right-10 z-10 hidden w-[300px] xl:bottom-32 rounded-2xl border border-white/[0.18] bg-[#0A2D20]/55 p-6 shadow-xl backdrop-blur-md lg:block xl:right-14">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">Votre projet, simplifié</p>
          <ul className="mt-4 space-y-3.5">
            {["Étude gratuite", "Sans avance de trésorerie", "Réponse sous 24 h"].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent">
                  <Check className="h-3.5 w-3.5 text-accent-foreground" strokeWidth={3} aria-hidden />
                </span>
                <span className="text-[15px]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="em-container relative z-10 w-full pb-16 pt-28 md:pb-20 md:pt-32 lg:pr-[52%]">
          <Link to="/professionnels/primes-cee" className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline">
            <ArrowLeft className="h-4 w-4" aria-hidden /> Primes CEE
          </Link>
          <div className="mt-9 flex items-center gap-3.5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-[1.5px] border-accent/70">
              <s.heroIcon className="h-[18px] w-[18px] text-accent" strokeWidth={2.2} aria-hidden />
            </span>
            <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-accent">{s.kw}</p>
          </div>
          <h1 className="font-display mt-5 max-w-[15ch] text-3xl font-extrabold tracking-tight md:text-5xl md:leading-[1.1] xl:text-[54px]">
            {s.accroche}
          </h1>
          <p className="mt-6 max-w-md text-lg font-light leading-relaxed text-primary-foreground/90">{s.chapo}</p>
          <div className="mt-9 flex flex-wrap items-center gap-7">
            <Button asChild size="lg" className="rounded-full bg-accent px-7 font-bold text-accent-foreground hover:bg-accent/90">
              <Link to="/contact?type=cee-pro">
                Estimer ma prime CEE <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Link to="/contact" className="border-b border-accent/70 pb-0.5 text-sm font-semibold text-accent transition-colors hover:border-accent">
              Parler à un expert CEE
            </Link>
          </div>
          {/* Réassurance (mobile) */}
          <ul className="mt-9 space-y-3 lg:hidden">
            {["Étude gratuite", "Sans avance de trésorerie", "Réponse sous 24 h"].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent">
                  <Check className="h-3 w-3 text-accent-foreground" strokeWidth={3} aria-hidden />
                </span>
                <span className="text-sm text-primary-foreground/90">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Opérations types */}
      <Section eyebrow="Opérations éligibles" title="Vos gisements CEE les plus valorisés" intro={s.intro}>
        <div className="grid gap-5 md:grid-cols-2">
          {s.operations.map((op, i) => (
            <TraceCard key={op.title} index={i} radius={16} accent={TRACE_COLORS[i % TRACE_COLORS.length]} className="rounded-2xl border border-border bg-card text-card-foreground h-full">
              <CardContent className="p-6">
                <h3 className="font-display text-lg font-bold text-primary">{op.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{op.text}</p>
              </CardContent>
            </TraceCard>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          Liste indicative — chaque site est unique : un conseiller identifie les fiches applicables et met en concurrence les offres de valorisation pour votre projet.
        </p>
      </Section>

      {/* CTA final */}
      <section className="em-container pb-14 md:pb-20">
        <div className="flex flex-col gap-8 rounded-3xl bg-primary px-8 py-12 text-primary-foreground md:flex-row md:items-center md:justify-between md:px-14">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Un projet dans votre secteur ?</h2>
            <p className="mt-3 max-w-xl text-primary-foreground/80">
              Estimation gratuite et sans engagement — nous comparons obligés et délégataires pour obtenir la meilleure valorisation de vos travaux.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="rounded-full bg-accent font-bold text-accent-foreground hover:bg-accent/90">
              <Link to="/contact?type=cee-pro">Estimer ma prime</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">
              <Link to="/contact">Être rappelé</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Ancienne URL /primes-cee/secteur/:slug → nouvelle adresse dans l'univers pro. */
export function RedirectSecteurCee() {
  const { slug } = useParams();
  return <Navigate to={`/professionnels/primes-cee/secteur/${slug}`} replace />;
}
